import { fetchSqlData, updateData } from '../../../services/api';
import type { AdmVehicle } from '../../../types';

type AssignmentRow = {
  id?: number;
  released_at?: string | null;
};

const normalizeRows = <T,>(data: any): T[] => {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.data) ? data.data : [];
};

export const getVehicleById = async (vehicleId: number): Promise<AdmVehicle | null> => {
  const rows = normalizeRows<AdmVehicle>(await fetchSqlData('adm_vehicles', 'id', vehicleId));
  return rows[0] || null;
};

export const syncVehicleAssignmentStatus = async (vehicleId: number) => {
  const [vehicle, assignmentRows] = await Promise.all([
    getVehicleById(vehicleId),
    fetchSqlData('adm_vehicle_assignments', 'vehicle_id', vehicleId),
  ]);

  if (!vehicle) return null;

  const assignments = normalizeRows<AssignmentRow>(assignmentRows);
  const hasActiveAssignment = assignments.some(row => !row.released_at);

  let nextStatus: AdmVehicle['status'] = vehicle.status;

  if (hasActiveAssignment) {
    nextStatus = 'Assigned';
  } else if (vehicle.status === 'Assigned') {
    nextStatus = 'Available';
  }

  if (nextStatus !== vehicle.status) {
    await updateData('adm_vehicles', vehicleId, { status: nextStatus });
  }

  return nextStatus;
};

export const setVehicleStatusIfNeeded = async (vehicleId: number, status: AdmVehicle['status']) => {
  const vehicle = await getVehicleById(vehicleId);
  if (!vehicle || vehicle.status === status) return status;

  await updateData('adm_vehicles', vehicleId, { status });
  return status;
};

export const syncVehicleUsageCounters = async (
  vehicleId: number,
  counters: {
    odometerReading?: number | null;
    hoursCounterReading?: number | null;
  }
) => {
  const vehicle = await getVehicleById(vehicleId);
  if (!vehicle) return null;

  const nextKilometer =
    counters.odometerReading != null &&
    Number.isFinite(counters.odometerReading) &&
    (vehicle.kilometer == null || counters.odometerReading >= vehicle.kilometer)
      ? counters.odometerReading
      : vehicle.kilometer;

  const nextHoursCounter =
    counters.hoursCounterReading != null &&
    Number.isFinite(counters.hoursCounterReading) &&
    (vehicle.hours_counter == null || counters.hoursCounterReading >= vehicle.hours_counter)
      ? counters.hoursCounterReading
      : vehicle.hours_counter;

  if (nextKilometer === vehicle.kilometer && nextHoursCounter === vehicle.hours_counter) {
    return {
      kilometer: vehicle.kilometer,
      hours_counter: vehicle.hours_counter,
    };
  }

  await updateData('adm_vehicles', vehicleId, {
    kilometer: nextKilometer ?? null,
    hours_counter: nextHoursCounter ?? null,
  });

  return {
    kilometer: nextKilometer,
    hours_counter: nextHoursCounter,
  };
};
